import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface PatientDeactivateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: Patient | null;
  onSuccess: () => void;
}

export function PatientDeactivateDialog({
  open,
  onOpenChange,
  patient,
  onSuccess,
}: PatientDeactivateDialogProps) {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleDeactivate = async () => {
    if (!patient || !user) return;

    setIsLoading(true);
    try {
      // Deactivate the patient
      const { error: updateError } = await supabase
        .from('patients')
        .update({ is_active: false })
        .eq('id', patient.id);

      if (updateError) throw updateError;

      // Log the action
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user.id,
        action: 'patient_deactivated',
        target_resource: 'patient',
        target_id: patient.id,
        metadata: {
          patient_name: `${patient.first_name} ${patient.last_name}`,
          patient_email: patient.email,
          reason: reason || 'No reason provided',
        },
      });

      toast({
        title: 'Patient Deactivated',
        description: `${patient.first_name} ${patient.last_name} has been deactivated. They can now be registered at another agency.`,
      });

      setReason('');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error deactivating patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to deactivate patient. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!patient || !user) return;

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('patients')
        .update({ is_active: true })
        .eq('id', patient.id);

      if (updateError) throw updateError;

      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user.id,
        action: 'patient_reactivated',
        target_resource: 'patient',
        target_id: patient.id,
        metadata: {
          patient_name: `${patient.first_name} ${patient.last_name}`,
        },
      });

      toast({
        title: 'Patient Reactivated',
        description: `${patient.first_name} ${patient.last_name} has been reactivated.`,
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error reactivating patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to reactivate patient. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!patient) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate Patient</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to deactivate{' '}
            <strong>{patient.first_name} {patient.last_name}</strong>?
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">
              Important Information:
            </p>
            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-1">
              <li>All existing patient data will remain with your agency</li>
              <li>The patient will no longer be able to access your agency's portal</li>
              <li>The patient can be registered at a new agency with a fresh record</li>
              <li>This action can be reversed by reactivating the patient</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Deactivation (Optional)</Label>
            <Textarea
              id="reason"
              placeholder="E.g., Patient transferred to another agency, Patient requested deactivation..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeactivate}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deactivating...
              </>
            ) : (
              'Deactivate Patient'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
